import { TRPCClientError } from "@trpc/client";
import type { AppRouter } from "@/trpc/routerTypes";

export type DomainBlockedReason = "consumer_domain" | "not_allowlisted";

type DomainBlockedShape = {
  domain: string;
  waitlisted: boolean;
  reason: DomainBlockedReason;
};

export type DomainBlockedClientError = TRPCClientError<AppRouter> & {
  data: { domainBlocked: DomainBlockedShape };
};

export function isDomainBlockedError(
  err: unknown,
): err is DomainBlockedClientError {
  if (!(err instanceof TRPCClientError)) return false;
  const data: unknown = err.data;
  if (typeof data !== "object" || data === null) return false;
  const blocked = (data as { domainBlocked?: unknown }).domainBlocked;
  if (typeof blocked !== "object" || blocked === null) return false;
  const b = blocked as {
    domain?: unknown;
    waitlisted?: unknown;
    reason?: unknown;
  };
  return (
    typeof b.domain === "string" &&
    typeof b.waitlisted === "boolean" &&
    (b.reason === "consumer_domain" || b.reason === "not_allowlisted")
  );
}

export type AccountDeletedClientError = TRPCClientError<AppRouter> & {
  data: { accountDeleted: true };
};

export function isAccountDeletedError(
  err: unknown,
): err is AccountDeletedClientError {
  if (!(err instanceof TRPCClientError)) return false;
  const data: unknown = err.data;
  if (typeof data !== "object" || data === null) return false;
  return (data as { accountDeleted?: unknown }).accountDeleted === true;
}

/**
 * True for any tRPC FORBIDDEN error. Some FORBIDDEN errors carry a
 * human-facing message (e.g. the `/team-login` email/password gate), but others
 * use internal/diagnostic wording (e.g. "Email is not verified" in
 * `functions/src/users/handler.ts`). Callers deciding whether to surface
 * `err.message` should scope this to the specific FORBIDDEN path they expect
 * (see `isEmailPasswordGateError`) and keep a generic fallback for the rest.
 */
export function isForbiddenError(
  err: unknown,
): err is TRPCClientError<AppRouter> {
  if (!(err instanceof TRPCClientError)) return false;
  const data: unknown = err.data;
  if (typeof data !== "object" || data === null) return false;
  return (data as { code?: unknown }).code === "FORBIDDEN";
}

/**
 * True only for a FORBIDDEN thrown by the email/password sign-in gate (#378),
 * tagged server-side via `EmailPasswordGateError` (see
 * `functions/src/users/errors.ts`). Its `.message` is intentionally user-facing
 * (`disabled` / `not_allowlisted` / verified-email required), so `/team-login`
 * can surface it directly — unlike a bare `isForbiddenError`, which also matches
 * internal/diagnostic FORBIDDENs that must NOT be shown to end users.
 */
export function isEmailPasswordGateError(
  err: unknown,
): err is TRPCClientError<AppRouter> {
  if (!(err instanceof TRPCClientError)) return false;
  const data: unknown = err.data;
  if (typeof data !== "object" || data === null) return false;
  return (data as { emailPasswordGate?: unknown }).emailPasswordGate === true;
}
